import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Loader2, Moon, RotateCcw, Save, Send, Sun } from "lucide-react";
import { GATES } from "@/data/temple";

const errText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg ?? "").join(" ") : "Could not save the letter.");
const FIELDS = [
  ["subject", "Subject line", 1],
  ["heading", "Opening line", 2],
  ["body", "Letter", 9],
  ["quote", "Highlighted blessing (optional)", 2],
  ["closing", "Closing words", 4],
];
const SECTIONS = [
  ["I · A Guided Reflection", [
    ["reflection_title", "Section title", 1],
    ["reflection_intro", "Introduction", 3],
    ["reflection_questions", "Reflection questions — one per line", 6],
    ["reflection_after", "After the questions", 3],
  ]],
  ["II · A Short Daily Practice", [
    ["practice_title", "Section title", 1],
    ["practice_intro", "Introduction", 3],
    ["practice_steps", "Practice steps — one per line", 7],
    ["practice_after", "After the steps", 3],
    ["practice_affirmation", "Affirmation (optional)", 2],
  ]],
  ["III · Consent & Self-Check Ritual", [
    ["consent_title", "Section title", 1],
    ["consent_intro", "Introduction", 4],
    ["consent_questions", "Self-check questions — one per line", 5],
    ["consent_partner", "With a partner", 4],
    ["consent_blessing", "Closing blessing (optional)", 2],
  ]],
  ["IV · The 13 Gates to Ecstasy", [
    ["gates_title", "Section title", 1],
    ["gates_intro", "Introduction", 3],
    ["tier1_title", "Gates 01–03 · group name", 1],
    ["tier1_desc", "Gates 01–03 · description", 2],
    ["tier2_title", "Gates 04–07 · group name", 1],
    ["tier2_desc", "Gates 04–07 · description", 2],
    ["tier3_title", "Gates 08–13 · group name", 1],
    ["tier3_desc", "Gates 08–13 · description", 2],
  ]],
  ["V · An Invitation", [
    ["invite_title", "Section title", 2],
    ["invite_body", "Workshop invitation", 5],
  ]],
];
const SECTION_KEYS = SECTIONS.flatMap(([, fs]) => fs.map(([f]) => f));
const keysFor = (k) => [...FIELDS.map(([f]) => f), ...(k === "starter" ? SECTION_KEYS : [])];
const toForm = (k, data) => (data ? Object.fromEntries(keysFor(k).map((f) => [f, data[f] ?? ""])) : null);
const toPayload = (k, form) => ({
  ...Object.fromEntries(FIELDS.map(([f]) => [f, form[f]])),
  sections: k === "starter" ? Object.fromEntries(SECTION_KEYS.map((f) => [f, form[f]])) : {},
});

const Field = ({ k, f, label, rows, form, setForm }) => (
  <label className="block mb-6">
    <span className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A]">{label}</span>
    <textarea data-testid={`letter-${k}-${f}`} rows={rows} value={form[f]} onChange={(e) => setForm((v) => ({ ...v, [f]: e.target.value }))} className={field} />
  </label>
);
const field = "mt-2 w-full bg-transparent border border-[#D9B47A]/25 p-3 text-[15px] leading-relaxed placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A] transition-colors resize-y";

const MoonOptInCount = ({ http, withRefresh }) => {
  const [c, setC] = useState(null);
  useEffect(() => {
    let alive = true;
    withRefresh(() => http.get("/admin/moon-reminders"))
      .then(({ data }) => alive && setC(data ?? null))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [http, withRefresh]);
  if (!c) return null;
  const on = c.on ?? 0;
  return (
    <div data-testid="moon-optin-count" className="mt-5 inline-flex items-baseline gap-3 border border-[#D9B47A]/25 bg-[#0D0908] px-5 py-3">
      <span data-testid="moon-optin-count-value" className="font-display text-3xl text-[#D9B47A]">{on}</span>
      <span className="text-sm text-[#CFC2B6]">of {c.initiates ?? 0} {c.initiates === 1 ? "initiate" : "initiates"} {on === 1 ? "has" : "have"} turned on moon reminders and will receive this email.</span>
    </div>
  );
};

const LetterEditor = ({ k, sv, title, when, http, withRefresh }) => {
  const [letter, setLetter] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [gate, setGate] = useState(0);
  const params = k === "reminder" ? { params: { gate } } : {};
  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await withRefresh(() => http.post(`/admin/letters/${k}/test`, null, params));
      toast.success(`Test sent to ${data?.to ?? "your inbox"}. It may take a minute to arrive.`);
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };
  const load = (data) => {
    setLetter(data ?? null);
    setForm(toForm(k, data));
  };
  useEffect(() => {
    withRefresh(() => http.get(`/admin/letters/${k}`, k === "reminder" ? { params: { gate } } : {}))
      .then(({ data }) => {
        setLetter(data ?? null);
        setForm((f) => f ?? toForm(k, data));
      })
      .catch(() => toast.error("Could not load the letter."));
  }, [k, gate, sv, http, withRefresh]);

  const save = async (reset) => {
    setBusy(true);
    try {
      const { data } = await withRefresh(() => (reset ? http.delete(`/admin/letters/${k}`, params) : http.put(`/admin/letters/${k}`, toPayload(k, form), params)));
      load(data);
      toast.success(reset ? "Restored the original wording." : "Saved. Every future letter will use this wording.");
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!form) return <p className="mt-10 text-[#A8998C] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Opening the letter…</p>;
  return (
    <section data-testid={`letter-editor-${k}`} className="mt-12 border-t border-[#D9B47A]/15 pt-10">
      <h2 className="font-display text-3xl sm:text-4xl">{title}</h2>
      <p className="mt-2 text-sm text-[#A8998C]">{when}</p>
      {k.startsWith("moon_") && <MoonOptInCount http={http} withRefresh={withRefresh} />}
      <div className="mt-8 grid gap-10">
        <div className="max-w-[700px]">
          {FIELDS.map(([f, label, rows]) => <Field key={f} k={k} f={f} label={label} rows={rows} form={form} setForm={setForm} />)}
          {k === "starter" && SECTIONS.map(([heading, fs]) => (
            <fieldset key={heading} data-testid={`letter-starter-section-${heading.split(" ")[0]}`} className="mb-6 border border-[#D9B47A]/15 p-5">
              <legend className="px-2 font-display text-xl text-[#F2E8DF]">{heading}</legend>
              {fs.map(([f, label, rows]) => <Field key={f} k={k} f={f} label={label} rows={rows} form={form} setForm={setForm} />)}
            </fieldset>
          ))}
          <p className="text-[13px] text-[#A8998C] leading-relaxed">Write <span className="text-[#F2E8DF]">{"{name}"}</span> for the person's first name{k === "reminder" ? <>, and <span className="text-[#F2E8DF]">{"{gate}"}</span> for the gate they're walking</> : null}. Wrap words in <span className="text-[#F2E8DF]">*stars*</span> to make them gold italic. Leave an empty line between paragraphs. Buttons and links are added for you. “Send me a test” emails the saved wording to your own inbox.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button data-testid={`letter-${k}-save`} disabled={busy} onClick={() => save(false)} className="inline-flex items-center gap-2 rounded-full bg-[#D9B47A] text-[#0A0706] px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save wording
            </button>
            {letter?.custom && (
              <button data-testid={`letter-${k}-reset`} disabled={busy} onClick={() => save(true)} className="inline-flex items-center gap-2 rounded-full border border-[#F2E8DF]/30 px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:border-[#D9B47A] hover:text-[#D9B47A] transition-colors">
                <RotateCcw className="w-4 h-4" /> Restore original
              </button>
            )}
            <button data-testid={`letter-${k}-test`} disabled={testing} onClick={sendTest} className="inline-flex items-center gap-2 rounded-full border border-[#C2414F]/70 px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#C2414F] transition-colors disabled:opacity-60">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send me a test
            </button>
          </div>
          {letter?.updated_at && <p data-testid={`letter-${k}-updated`} className="mt-3 font-tt-mono text-[11px] uppercase tracking-[0.15em] text-[#A8998C]">Your wording · saved {new Date(letter.updated_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</p>}
        </div>
        <div>
          {k === "reminder" && (
            <label className="mb-4 flex flex-wrap items-center gap-3">
              <span className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A]">Preview &amp; test for</span>
              <select data-testid="letter-reminder-gate" value={gate} onChange={(e) => setGate(Number(e.target.value))} className="bg-[#0D0908] border border-[#D9B47A]/40 rounded-full px-4 py-2 text-sm text-[#F2E8DF] focus:outline-none focus:border-[#D9B47A]">
                {GATES.map((g, i) => <option key={g.name} value={i}>Gate {String(i + 1).padStart(2, "0")} · {g.name}</option>)}
              </select>
            </label>
          )}
          <p className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#A8998C]">Saved preview · {letter?.preview_subject}</p>
          <iframe data-testid={`letter-${k}-preview`} title={`${title} preview`} sandbox="" srcDoc={letter?.html ?? ""} className="mt-2 w-full max-w-[700px] h-[760px] border border-[#D9B47A]/20 bg-[#0A0706]" />
        </div>
      </div>
    </section>
  );
};

const LETTERS = [
  ["starter", "Starter Guide", "Sent to every seeker who asks for the free Starter Guide (or “all of it”). Everything is editable except the links to each gate and your contact line."],
  ["welcome", "Welcome letter", "Sent once, the moment someone’s initiation payment completes."],
  ["reminder", "Weekly reminder", "Sent every Sunday at 9am Sydney time to readers who confirmed weekly practice reminders."],
  ["nudge", "Gentle nudge", "Sent once to people who started initiation 1–7 days ago but didn’t finish paying."],
  ["mastery", "Mastery letter", "Sent once, with their certificate, when an initiate completes all 13 gates."],
  ["anniversary", "Anniversary blessing", "Sent once, one year after someone becomes an initiate (checked every morning at 11am Sydney time)."],
  ["seasonal", "Seasonal blessing", "A one-off blessing you send yourself — for a full moon, solstice or any sacred moment — to every initiate. Save your wording, send yourself a test, then send it to all."],
  ["moon_new", "New moon email", "Sent on new moon nights (checked every morning at 11am Sydney time) to initiates who turned on moon reminders on My Journey. If they wrote a moon note last cycle, their last intention and note are shown back to them. Use {name} for their first name."],
  ["moon_full", "Full moon email", "Sent on full moon nights to initiates who turned on moon reminders. If they planted an intention at the new moon, it is shown back to them in the email."],
  ["moon", "Full moon practice", ""],
  ["newmoon", "New moon practice", ""],
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");
const toLocalInput = (iso) => {
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const SacredCalendar = ({ http, withRefresh, scheduled, onPick }) => {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    withRefresh(() => http.get("/admin/sacred-calendar"))
      .then(({ data }) => setEvents(Array.isArray(data?.events) ? data.events : []))
      .catch(() => {});
  }, [http, withRefresh]);
  if (!events.length) return null;
  const near = (at) => scheduled.some((s) => Math.abs(new Date(s.send_at) - new Date(at)) < 36 * 3600 * 1000);
  return (
    <section data-testid="sacred-calendar" className="mt-10 max-w-[700px]">
      <p className="font-tt-mono text-[12px] uppercase tracking-[0.3em] text-[#D9B47A]">Sacred calendar · the year ahead</p>
      <p className="mt-2 text-sm text-[#A8998C]">Full moons, solstices and equinoxes, shown in your local time. Seasons are named for the southern hemisphere.</p>
      <ul className="mt-5 border-t border-[#D9B47A]/15">
        {events.map((e) => (
          <li key={e.at} data-testid={`sacred-event-${e.at.slice(0, 10)}`} className="py-4 border-b border-[#D9B47A]/15 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              {e.kind === "full_moon" ? <Moon className="w-5 h-5 text-[#F2E8DF]" /> : <Sun className="w-5 h-5 text-[#D9B47A]" />}
              <div>
                <p className="font-display text-xl">{e.name}</p>
                <p className="text-sm text-[#A8998C]">{new Date(e.at).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "long", year: "numeric", hour: "numeric", minute: "2-digit" })}</p>
              </div>
            </div>
            {near(e.at) ? (
              <span className="font-tt-mono text-[11px] uppercase tracking-[0.15em] text-[#D9B47A]">Blessing scheduled</span>
            ) : (
              <button data-testid={`sacred-pick-${e.at.slice(0, 10)}`} onClick={() => onPick(e.at)} className="rounded-full border border-[#D9B47A]/40 px-4 py-2 text-sm hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors">Schedule for this moment</button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

const BlessingSender = ({ http, withRefresh }) => {
  const [info, setInfo] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [when, setWhen] = useState("");
  const schedule = async () => {
    setBusy(true);
    try {
      await withRefresh(() => http.post("/admin/blessings/schedule", { send_at: new Date(when).toISOString() }));
      toast.success("Blessing scheduled with your saved wording.");
      setWhen("");
      refresh();
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const cancel = async (id) => {
    try {
      await withRefresh(() => http.delete(`/admin/blessings/${id}`));
      toast.success("Scheduled blessing cancelled.");
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    }
    refresh();
  };
  const pickMoment = (iso) => {
    setWhen(toLocalInput(iso));
    document.querySelector('[data-testid="blessing-schedule-at"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const refresh = useCallback(() => {
    withRefresh(() => http.get("/admin/blessings"))
      .then(({ data }) => setInfo(data ?? null))
      .catch(() => {});
  }, [http, withRefresh]);
  useEffect(() => {
    refresh();
  }, [refresh]);
  const sending = (info?.history ?? []).some((h) => h.status === "sending");
  useEffect(() => {
    if (!sending) return undefined;
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [sending, refresh]);
  const send = async () => {
    setBusy(true);
    try {
      await withRefresh(() => http.post("/admin/blessings/send", { expected: info.recipients }));
      toast.success("Your blessing is on its way to every initiate.");
      setConfirming(false);
      refresh();
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
      refresh();
    } finally {
      setBusy(false);
    }
  };
  if (!info) return null;
  const n = info.recipients ?? 0;
  const all = info.history ?? [];
  const scheduled = all.filter((h) => h.status === "scheduled").sort((a, b) => a.send_at.localeCompare(b.send_at));
  const past = all.filter((h) => h.status !== "scheduled").sort((a, b) => (b.started_at ?? b.created_at).localeCompare(a.started_at ?? a.created_at));
  return (
    <>
    <section data-testid="blessing-sender" className="mt-10 border border-[#C2414F]/40 bg-[#0D0908] p-6 sm:p-8 max-w-[700px]">
      <p className="font-tt-mono text-[12px] uppercase tracking-[0.3em] text-[#D9B47A]">Send to all initiates</p>
      <p data-testid="blessing-recipients" className="mt-3 font-display text-2xl">{n} initiate{n === 1 ? "" : "s"} will receive the saved blessing.</p>
      {info.opted_out > 0 && <p className="mt-1 text-sm text-[#A8998C]">{info.opted_out} chose to stop seasonal blessings and won’t receive it.</p>}
      <p className="mt-2 text-sm text-[#A8998C]">Each email greets them by first name and includes a link to stop seasonal blessings.</p>
      {!confirming ? (
        <button data-testid="blessing-send" disabled={n === 0 || sending} onClick={() => setConfirming(true)} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#C2414F] text-[#F2E8DF] px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors disabled:opacity-50">
          <Send className="w-4 h-4" /> Send blessing to all
        </button>
      ) : (
        <div data-testid="blessing-confirm" className="mt-6 flex flex-wrap items-center gap-3">
          <p className="w-full text-[#F2E8DF]">Send this blessing to {n} initiate{n === 1 ? "" : "s"} now? This can’t be undone.</p>
          <button data-testid="blessing-confirm-send" disabled={busy} onClick={send} className="inline-flex items-center gap-2 rounded-full bg-[#C2414F] text-[#F2E8DF] px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Yes, send to {n}
          </button>
          <button data-testid="blessing-cancel" onClick={() => setConfirming(false)} className="rounded-full border border-[#F2E8DF]/30 px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:border-[#D9B47A]">Cancel</button>
        </div>
      )}
      <div className="mt-8 border-t border-[#D9B47A]/15 pt-6">
        <p className="font-tt-mono text-[12px] uppercase tracking-[0.3em] text-[#D9B47A]">Or schedule it</p>
        <p className="mt-2 text-sm text-[#A8998C]">Your saved wording is kept exactly as it is now, so you can write the next blessing straight away. It goes to everyone who is an initiate at that moment, within 15 minutes of the time you choose.</p>
        <div className="mt-4 flex flex-wrap gap-3 items-center">
          <input data-testid="blessing-schedule-at" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="bg-transparent border border-[#D9B47A]/40 rounded-full px-4 py-2.5 text-sm text-[#F2E8DF] [color-scheme:dark] focus:outline-none focus:border-[#D9B47A]" />
          <button data-testid="blessing-schedule" disabled={!when || busy} onClick={schedule} className="inline-flex items-center gap-2 rounded-full bg-[#D9B47A] text-[#0A0706] px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors disabled:opacity-50">
            <CalendarClock className="w-4 h-4" /> Schedule blessing
          </button>
        </div>
        {scheduled.length > 0 && (
          <ul data-testid="blessing-scheduled" className="mt-6">
            {scheduled.map((h) => (
              <li key={h.id} data-testid={`blessing-scheduled-${h.id}`} className="py-3 border-b border-[#D9B47A]/15 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display text-lg">{h.subject}</p>
                  <p className="font-tt-mono text-[11px] uppercase tracking-[0.15em] text-[#D9B47A] mt-1">Scheduled · {fmtDate(h.send_at)}</p>
                </div>
                <button data-testid={`blessing-cancel-${h.id}`} onClick={() => cancel(h.id)} className="rounded-full border border-[#F2E8DF]/30 px-4 py-2 text-sm hover:border-[#C2414F] hover:text-[#E8838E] transition-colors">Cancel</button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {past.length > 0 && (
        <ul data-testid="blessing-history" className="mt-8 border-t border-[#D9B47A]/15">
          {past.map((h) => (
            <li key={h.id} data-testid={`blessing-history-${h.id}`} className="py-4 border-b border-[#D9B47A]/15">
              <p className="font-display text-lg">{h.subject}</p>
              <p className="font-tt-mono text-[11px] uppercase tracking-[0.15em] text-[#A8998C] mt-1">
                {fmtDate(h.started_at ?? h.created_at)} · {h.status === "sending" ? `Sending ${h.sent + h.failed}/${h.total}` : `Sent to ${h.sent} of ${h.total}`}{h.failed ? ` · ${h.failed} failed` : ""}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
    <SacredCalendar http={http} withRefresh={withRefresh} scheduled={scheduled} onPick={pickMoment} />
    </>
  );
};

const SIGNOFF_CHOICES = ["With love,", "In devotion,", "Blessed Be,", "With reverence,", "In pleasure and presence,"];

const SignoffEditor = ({ http, withRefresh, onSaved }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    withRefresh(() => http.get("/admin/signoff"))
      .then(({ data }) => setS(data ?? null))
      .catch(() => toast.error("Could not load the sign-off."));
  }, [http, withRefresh]);
  if (!s) return null;
  const set = (f) => (e) => setS((v) => ({ ...v, [f]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await withRefresh(() => http.put("/admin/signoff", { line: s.line, name: s.name, title: s.title }));
      setS(data);
      onSaved();
      toast.success("Sign-off saved. Every email will now close this way.");
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const input = "mt-2 w-full bg-transparent border border-[#D9B47A]/25 px-3 py-2.5 text-[15px] focus:outline-none focus:border-[#D9B47A] transition-colors";
  return (
    <section data-testid="signoff-editor" className="mt-10 border border-[#D9B47A]/25 bg-[#0D0908] p-6 sm:p-8">
      <p className="font-tt-mono text-[12px] uppercase tracking-[0.3em] text-[#D9B47A]">Sign-off · used on every email</p>
      <div className="mt-5 flex flex-wrap gap-2">
        {SIGNOFF_CHOICES.map((c) => (
          <button key={c} data-testid={`signoff-choice-${c.replace(/\W+/g, "-").toLowerCase()}`} onClick={() => setS((v) => ({ ...v, line: c }))} className={`rounded-full border px-4 py-2 text-sm transition-colors ${s.line === c ? "bg-[#D9B47A] border-[#D9B47A] text-[#0A0706]" : "border-[#D9B47A]/30 text-[#CFC2B6] hover:border-[#D9B47A]"}`}>{c}</button>
        ))}
      </div>
      <div className="mt-5 grid sm:grid-cols-3 gap-4">
        <label><span className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#A8998C]">Closing line</span><input data-testid="signoff-line" maxLength={60} value={s.line} onChange={set("line")} className={input} /></label>
        <label><span className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#A8998C]">Signed as</span><input data-testid="signoff-name" maxLength={60} value={s.name} onChange={set("name")} className={input} /></label>
        <label><span className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#A8998C]">Title (optional)</span><input data-testid="signoff-title" maxLength={120} value={s.title} onChange={set("title")} className={input} /></label>
      </div>
      <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
        <p data-testid="signoff-preview" className="font-display italic text-xl text-[#D9B47A] leading-snug">{s.line}<br />{s.name}{s.title ? <span className="block not-italic font-sans text-[12px] text-[#A8998C] mt-1">{s.title}</span> : null}</p>
        <button data-testid="signoff-save" disabled={busy} onClick={save} className="inline-flex items-center gap-2 rounded-full bg-[#D9B47A] text-[#0A0706] px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save sign-off
        </button>
      </div>
    </section>
  );
};

const MOON_COPY = {
  full: ["Full moon practice", "On full moon nights a slim banner at the top of the site invites visitors to this practice; it opens when they tap “Begin the practice”. It isn’t emailed.", "fullmoon", "Saved. Visitors will see this on the next full moon."],
  new: ["New moon practice", "On new moon nights a slim banner at the top of the site invites visitors to set an intention; this practice opens when they tap “Begin the practice”. It isn’t emailed.", "newmoon", "Saved. Visitors will see this on the next new moon."],
};

const SettingSwitch = ({ id, title, desc, on, onChange, busy }) => (
  <div data-testid={`${id}-control`} className="mt-4 border border-[#D9B47A]/25 bg-[#0D0908] p-5 flex items-center justify-between gap-6">
    <div>
      <p className="font-display text-xl">{title}</p>
      <p className="mt-1 text-sm text-[#A8998C]">{desc}</p>
    </div>
    <button data-testid={`${id}-toggle`} role="switch" aria-checked={on} disabled={busy} onClick={() => onChange(!on)} className={`relative shrink-0 w-14 h-8 rounded-full border transition-colors ${on ? "bg-[#D9B47A] border-[#D9B47A]" : "bg-transparent border-[#A8998C]/50"}`}>
      <span className={`absolute top-1 left-1 w-6 h-6 rounded-full transition-transform ${on ? "translate-x-6 bg-[#0A0706]" : "translate-x-0 bg-[#A8998C]"}`} />
      <span className="sr-only">{on ? "On" : "Off"}</span>
    </button>
  </div>
);

const MoonPracticeEditor = ({ kind, http, withRefresh }) => {
  const [title, intro, previewParam, savedMsg] = MOON_COPY[kind];
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    withRefresh(() => http.get(`/admin/moon-practice/${kind}`))
      .then(({ data }) => setP(data ?? null))
      .catch(() => toast.error("Could not load the practice."));
  }, [kind, http, withRefresh]);
  const setting = async (key, on, msg) => {
    setBusy(true);
    try {
      const { data } = await withRefresh(() => http.put("/admin/site-settings", { [key]: on }));
      setP((v) => ({ ...v, ...data }));
      toast.success(msg(data?.[key]));
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const cardKey = kind === "full" ? "full_card" : "new_card";
  const save = async (reset) => {
    setBusy(true);
    try {
      const { data } = await withRefresh(() => (reset ? http.delete(`/admin/moon-practice/${kind}`) : http.put(`/admin/moon-practice/${kind}`, { heading: p.heading, steps: p.steps, farewell: p.farewell })));
      setP(data);
      toast.success(reset ? "Restored the original practice." : savedMsg);
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  if (!p) return <p className="mt-10 text-[#A8998C] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Opening the practice…</p>;
  const set = (f) => (e) => setP((v) => ({ ...v, [f]: e.target.value }));
  return (
    <section data-testid={`moon-practice-editor-${kind}`} className="mt-12 border-t border-[#D9B47A]/15 pt-10 max-w-[700px]">
      <h2 className="font-display text-3xl sm:text-4xl">{title}</h2>
      <p className="mt-2 text-sm text-[#A8998C]">{intro} You can see it at any time by adding ?{previewParam}=preview to any page address.</p>
      <div className="mt-6">
        <SettingSwitch id={`${kind}-card`} title={`Show the ${kind} moon invitation`} desc={`The banner and practice card on ${kind} moon nights.`} on={p[cardKey] !== false} busy={busy} onChange={(on) => setting(cardKey, on, (v) => (v ? `The ${kind} moon card is on.` : `The ${kind} moon card is off. Visitors won’t see it.`))} />
        {kind === "full" && <SettingSwitch id="moon-glow" title="Silver moonglow" desc="A soft silver glow across the site on full moon nights." on={p.moon_glow !== false} busy={busy} onChange={(on) => setting("moon_glow", on, (v) => (v ? "The silver moonglow is on for full moon nights." : "The silver moonglow is off."))} />}
      </div>
      <label className="block mt-8 mb-6"><span className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A]">Invitation</span><textarea data-testid="moon-practice-heading" rows={2} value={p.heading} onChange={set("heading")} className={field} /></label>
      <label className="block mb-6"><span className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A]">Practice steps — one per line (up to 8)</span><textarea data-testid="moon-practice-steps" rows={8} value={p.steps} onChange={set("steps")} className={field} /></label>
      <label className="block mb-6"><span className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A]">Closing button</span><input data-testid="moon-practice-farewell" maxLength={40} value={p.farewell} onChange={set("farewell")} className={field} /></label>
      <p className="text-[13px] text-[#A8998C]">Wrap words in <span className="text-[#F2E8DF]">*stars*</span> to make them gold italic.</p>
      <div className="mt-6 flex flex-wrap gap-3">
        <button data-testid="moon-practice-save" disabled={busy} onClick={() => save(false)} className="inline-flex items-center gap-2 rounded-full bg-[#D9B47A] text-[#0A0706] px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save practice</button>
        {p.custom && <button data-testid="moon-practice-reset" disabled={busy} onClick={() => save(true)} className="inline-flex items-center gap-2 rounded-full border border-[#F2E8DF]/30 px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:border-[#D9B47A] hover:text-[#D9B47A] transition-colors"><RotateCcw className="w-4 h-4" /> Restore original</button>}
        <a data-testid="moon-practice-preview" href={`/?${previewParam}=preview`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-[#D9B47A]/40 px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors"><Moon className="w-4 h-4" /> Preview on site</a>
      </div>
    </section>
  );
};

export default function LettersAdmin({ http, withRefresh }) {
  const [pick, setPick] = useState("starter");
  const [sv, setSv] = useState(0);
  const [, title, when] = LETTERS.find(([k]) => k === pick);
  return (
    <div data-testid="letters-admin" className="mt-10">
      <SignoffEditor http={http} withRefresh={withRefresh} onSaved={() => setSv((n) => n + 1)} />
      <div className="mt-10 flex flex-wrap gap-2">
        {LETTERS.map(([k, l]) => (
          <button key={k} data-testid={`letters-pick-${k}`} onClick={() => setPick(k)} className={`rounded-full border px-4 py-2 text-sm transition-colors ${pick === k ? "bg-[#D9B47A] border-[#D9B47A] text-[#0A0706]" : "border-[#D9B47A]/30 text-[#CFC2B6] hover:border-[#D9B47A]"}`}>{l}</button>
        ))}
      </div>
      {pick === "moon" || pick === "newmoon" ? <MoonPracticeEditor key={pick} kind={pick === "moon" ? "full" : "new"} http={http} withRefresh={withRefresh} /> : <LetterEditor key={pick} k={pick} sv={sv} title={title} when={when} http={http} withRefresh={withRefresh} />}
      {pick === "seasonal" && <BlessingSender http={http} withRefresh={withRefresh} />}
    </div>
  );
}
